(function () {
  class StudioHeaderV2 extends HTMLElement {
    constructor() {
      super();
      this.activeLayer = null;
      this.lastFocusedElement = null;
      this.searchTimer = null;
      this.searchController = null;
      this.closeTimer = null;
      this.hoverMedia = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.onScroll = this.onScroll.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
    }

    connectedCallback() {
      this.shopTriggers = Array.from(this.querySelectorAll('[data-shop-trigger]'));
      this.shopPanel = this.querySelector('[data-shop-panel]');
      this.searchTriggers = Array.from(this.querySelectorAll('[data-search-trigger]'));
      this.searchPanel = this.querySelector('[data-search-panel]');
      this.aboutPanel = this.querySelector('[data-about-panel]');
      this.searchInput = this.querySelector('[data-search-input]');
      this.searchDefault = this.querySelector('[data-search-default]');
      this.searchLive = this.querySelector('[data-search-live]');
      this.searchStatus = this.querySelector('[data-search-status]');
      this.backdrop = this.querySelector('[data-layer-backdrop]');
      this.aboutTriggers = Array.from(document.querySelectorAll('[data-about-trigger]'));
      this.aboutTriggerHandlers = new Map();

      document.body.classList.toggle('studio-inner-page', this.dataset.home !== 'true');

      this.shopTriggers.forEach((trigger) => {
        trigger.addEventListener('click', () => this.toggleMenu(trigger));

        trigger.addEventListener('mouseenter', () => {
          if (!this.hoverMedia.matches) return;
          this.cancelScheduledClose();
          this.openMenu(trigger);
        });

        trigger.addEventListener('mouseleave', () => {
          if (this.hoverMedia.matches) this.scheduleClose();
        });
      });

      this.shopPanel?.addEventListener('mouseenter', () => this.cancelScheduledClose());
      this.shopPanel?.addEventListener('mouseleave', () => {
        if (this.hoverMedia.matches) this.scheduleClose();
      });

      this.querySelectorAll('[data-preview-target]').forEach((link) => {
        const activate = () => this.showPreview(link.dataset.previewTarget);
        link.addEventListener('mouseenter', activate);
        link.addEventListener('focus', activate);
        link.addEventListener('click', (event) => {
          if (this.hoverMedia.matches) return;
          if (!link.classList.contains('is-active')) {
            event.preventDefault();
            activate();
            this.querySelector(`[data-preview-pane="${CSS.escape(link.dataset.previewTarget)}"]`)
              ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        });
      });

      this.searchTriggers.forEach((trigger) => {
        trigger.addEventListener('click', () => this.openSearch(trigger));
      });

      this.aboutTriggers.forEach((trigger) => {
        const handler = (event) => {
          event.preventDefault();
          this.openAbout(trigger);
        };
        this.aboutTriggerHandlers.set(trigger, handler);
        trigger.addEventListener('click', handler);
      });

      this.querySelectorAll('[data-layer-close]').forEach((button) => {
        button.addEventListener('click', () => this.closeLayer());
      });

      this.backdrop?.addEventListener('click', () => this.closeLayer());
      this.searchInput?.addEventListener('input', () => this.queueSearch());
      document.addEventListener('keydown', this.onKeydown);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.updateScrolledState();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('scroll', this.onScroll);
      window.clearTimeout(this.searchTimer);
      window.clearTimeout(this.closeTimer);
      this.searchController?.abort();
      this.aboutTriggerHandlers?.forEach((handler, trigger) => trigger.removeEventListener('click', handler));
      this.aboutTriggerHandlers?.clear();
      document.body.classList.remove('studio-layer-open', 'studio-inner-page');
    }

    onScroll() {
      if (this.scrollTicking) return;
      this.scrollTicking = true;
      window.requestAnimationFrame(() => {
        this.updateScrolledState();
        this.scrollTicking = false;
      });
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.activeLayer) this.closeLayer();
    }

    updateScrolledState() {
      this.dataset.scrolled = String(window.scrollY > 24);
    }

    toggleMenu(trigger) {
      if (this.activeLayer === 'menu') {
        this.closeLayer();
        return;
      }
      this.openMenu(trigger);
    }

    openMenu(trigger) {
      this.cancelScheduledClose();
      if (this.activeLayer === 'menu') return;

      this.closeLayer(false);
      if (!this.shopPanel) return;

      this.activeLayer = 'menu';
      this.lastFocusedElement = trigger;
      this.shopPanel.hidden = false;
      this.shopTriggers.forEach((button) => button.setAttribute('aria-expanded', 'true'));
      this.openLayerState();

      const firstPreviewLink =
        this.shopPanel.querySelector('[data-preview-target].is-active') ||
        this.shopPanel.querySelector('[data-preview-target]');
      if (firstPreviewLink) this.showPreview(firstPreviewLink.dataset.previewTarget);
    }

    scheduleClose() {
      this.cancelScheduledClose();
      this.closeTimer = window.setTimeout(() => {
        if (this.activeLayer === 'menu') this.closeLayer(false);
      }, 180);
    }

    cancelScheduledClose() {
      window.clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }

    openSearch(trigger) {
      this.cancelScheduledClose();

      if (this.activeLayer === 'search') {
        this.closeLayer();
        return;
      }

      this.closeLayer(false);
      this.activeLayer = 'search';
      this.lastFocusedElement = trigger.closest('[data-shop-panel]')
        ? this.querySelector('.studio-header__mobile-menu-trigger')
        : trigger;

      if (!this.searchPanel) return;
      this.searchPanel.hidden = false;
      this.searchTriggers.forEach((button) => button.setAttribute('aria-expanded', 'true'));
      this.openLayerState();
      window.requestAnimationFrame(() => this.searchInput?.focus());
    }

    openAbout(trigger) {
      this.cancelScheduledClose();

      if (this.activeLayer === 'about') {
        this.closeLayer();
        return;
      }

      this.closeLayer(false);
      if (!this.aboutPanel) return;

      this.activeLayer = 'about';
      this.lastFocusedElement = trigger;
      this.aboutPanel.hidden = false;
      this.aboutTriggers.forEach((button) => button.setAttribute('aria-expanded', 'true'));
      this.openLayerState();
      window.requestAnimationFrame(() => this.aboutPanel.querySelector('[data-layer-close]')?.focus());
    }

    openLayerState() {
      this.classList.add('is-layer-open');
      if (this.backdrop) this.backdrop.hidden = false;
      document.body.classList.add('studio-layer-open');
    }

    closeLayer(restoreFocus = true) {
      this.cancelScheduledClose();

      if (this.shopPanel) this.shopPanel.hidden = true;
      if (this.searchPanel) this.searchPanel.hidden = true;
      if (this.aboutPanel) this.aboutPanel.hidden = true;
      if (this.backdrop) this.backdrop.hidden = true;

      this.shopTriggers?.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
      this.searchTriggers?.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
      this.aboutTriggers?.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
      this.classList.remove('is-layer-open');
      document.body.classList.remove('studio-layer-open');

      const focusTarget = this.lastFocusedElement;
      this.activeLayer = null;
      this.lastFocusedElement = null;
      if (restoreFocus && focusTarget instanceof HTMLElement) focusTarget.focus();
    }

    showPreview(targetId) {
      if (!this.shopPanel || !targetId) return;

      this.shopPanel.querySelectorAll('[data-preview-pane]').forEach((pane) => {
        pane.hidden = pane.dataset.previewPane !== targetId;
      });

      this.shopPanel.querySelectorAll('[data-preview-target]').forEach((link) => {
        link.classList.toggle('is-active', link.dataset.previewTarget === targetId);
      });
    }

    queueSearch() {
      window.clearTimeout(this.searchTimer);
      const query = this.searchInput?.value.trim() || '';

      if (!query) {
        this.searchController?.abort();
        if (this.searchDefault) this.searchDefault.hidden = false;
        if (this.searchLive) this.searchLive.hidden = true;
        if (this.searchStatus) this.searchStatus.textContent = '';
        return;
      }

      this.searchTimer = window.setTimeout(() => this.runSearch(query), 220);
    }

    async runSearch(query) {
      this.searchController?.abort();
      this.searchController = new AbortController();
      if (this.searchStatus) this.searchStatus.textContent = 'Searching';

      try {
        const root = window.Shopify?.routes?.root || '/';
        const endpoint = `${root}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last`;
        const response = await fetch(endpoint, {
          signal: this.searchController.signal,
          headers: { Accept: 'application/json' },
        });

        if (!response.ok) throw new Error(`Search failed with ${response.status}`);
        const data = await response.json();
        const products = data.resources?.results?.products || [];
        this.renderSearchResults(products, query);
      } catch (error) {
        if (error.name === 'AbortError') return;
        if (this.searchDefault) this.searchDefault.hidden = true;
        if (this.searchLive) {
          this.searchLive.hidden = false;
          this.searchLive.replaceChildren(this.createEmptyMessage('Search is having a moment. Try again.'));
        }
        if (this.searchStatus) this.searchStatus.textContent = 'Search unavailable';
      }
    }

    renderSearchResults(products, query) {
      if (!this.searchLive || !this.searchDefault) return;

      this.searchLive.replaceChildren();
      this.searchDefault.hidden = true;
      this.searchLive.hidden = false;

      if (!products.length) {
        this.searchLive.append(this.createEmptyMessage(`Nothing for “${query}”.`));
        if (this.searchStatus) this.searchStatus.textContent = 'No products found';
        return;
      }

      products.forEach((product) => this.searchLive.append(this.createProductCard(product)));
      if (this.searchStatus) this.searchStatus.textContent = `${products.length} products found`;
    }

    createProductCard(product) {
      const link = document.createElement('a');
      link.className = 'studio-search-card';
      link.href = product.url;

      const media = document.createElement('span');
      media.className = 'studio-search-card__media';
      if (product.featured_image?.url) {
        const image = document.createElement('img');
        image.src = product.featured_image.url;
        image.alt = product.featured_image.alt || product.title;
        image.loading = 'lazy';
        media.append(image);
      }

      const title = document.createElement('span');
      title.className = 'studio-search-card__title';
      title.textContent = product.title;

      const price = document.createElement('span');
      price.className = 'studio-search-card__price';
      price.textContent = this.formatMoney(product.price);

      link.append(media, title, price);
      return link;
    }

    createEmptyMessage(message) {
      const element = document.createElement('p');
      element.className = 'studio-search__empty';
      element.textContent = message;
      return element;
    }

    formatMoney(value) {
      const amount = Number.parseFloat(value);
      const currency = window.Shopify?.currency?.active || 'PKR';
      if (!Number.isFinite(amount)) return value || '';

      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency,
          currencyDisplay: 'code',
        }).format(amount);
      } catch (_error) {
        return `${currency} ${amount.toFixed(2)}`;
      }
    }
  }

  if (!customElements.get('studio-header-v2')) {
    customElements.define('studio-header-v2', StudioHeaderV2);
  }
})();
